/** The drawn pieces of the opportunity matrix (OpportunityMatrix): frame, dots, tooltip and legend. */

import { formatScore } from '@/ui/ScoreValue';
import type { MatrixPoint } from './matrixAxes';
import { DOT_KINDS, DOT_R, type DotKind, MARGIN, type MatrixScale, QUADRANTS, dotKind, dotKindLabel, markPath } from './matrixGeometry';

function Swatch({ kind, colour }: { kind: DotKind; colour?: string }) {
  return (
    <svg className={`opp-swatch dot-${kind}${colour ? ` ${colour}` : ''}`} width="14" height="14" aria-hidden="true">
      <path className="opp-mark" d={markPath(kind, 7, 7, 4.5)} />
    </svg>
  );
}

function QuadrantLabels({ scale }: { scale: MatrixScale }) {
  const { plotW, plotH, narrow } = scale;
  return (
    <>
      {/* Narrow plots keep only the quadrant that matters, so labels never collide. */}
      {!narrow && (
        <text className="opp-quadrant" x={MARGIN.left + 8} y={MARGIN.top + 18}>
          {QUADRANTS.topLeft}
        </text>
      )}
      <text className="opp-quadrant is-priority" x={MARGIN.left + plotW - 8} y={MARGIN.top + 18} textAnchor="end">
        {QUADRANTS.topRight}
      </text>
      {!narrow && (
        <>
          <text className="opp-quadrant" x={MARGIN.left + 8} y={MARGIN.top + plotH - 8}>
            {QUADRANTS.bottomLeft}
          </text>
          <text className="opp-quadrant" x={MARGIN.left + plotW - 8} y={MARGIN.top + plotH - 8} textAnchor="end">
            {QUADRANTS.bottomRight}
          </text>
        </>
      )}
    </>
  );
}

/** Everything behind the dots: the priority quadrant, the grid and ticks, the threshold lines and the labels. */
export function MatrixFrame({ scale }: { scale: MatrixScale }) {
  const { x, y, tx, ty, plotW, plotH, height, ticks } = scale;
  return (
    <>
      <rect className="opp-priority" x={tx} y={MARGIN.top} width={x(100) - tx} height={ty - MARGIN.top} />
      {ticks.map((t) => (
        <g key={t} aria-hidden="true">
          <line className="opp-grid" x1={x(t)} x2={x(t)} y1={MARGIN.top} y2={MARGIN.top + plotH} />
          <line className="opp-grid" x1={MARGIN.left} x2={MARGIN.left + plotW} y1={y(t)} y2={y(t)} />
          <text className="opp-tick" x={x(t)} y={MARGIN.top + plotH + 16} textAnchor="middle">
            {t}
          </text>
          <text className="opp-tick" x={MARGIN.left - 8} y={y(t) + 4} textAnchor="end">
            {t}
          </text>
        </g>
      ))}
      <g aria-hidden="true">
        <line className="opp-threshold" x1={tx} x2={tx} y1={MARGIN.top} y2={MARGIN.top + plotH} />
        <line className="opp-threshold" x1={MARGIN.left} x2={MARGIN.left + plotW} y1={ty} y2={ty} />
        <QuadrantLabels scale={scale} />
        <text className="opp-axis-title" x={MARGIN.left + plotW / 2} y={height - 6} textAnchor="middle">
          Impact →
        </text>
        <text
          className="opp-axis-title"
          transform={`translate(14 ${MARGIN.top + plotH / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          Feasibility →
        </text>
      </g>
    </>
  );
}

export function MatrixDot({
  point: p,
  scale,
  active,
  colour,
  onActivate,
  onDeactivate,
  onOpen,
}: {
  point: MatrixPoint;
  scale: MatrixScale;
  active: boolean;
  /** The industry colour class, or '' when colouring by verdict. */
  colour: string;
  onActivate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onOpen: (id: string) => void;
}) {
  const kind = dotKind(p);
  const cx = scale.x(p.impact);
  const cy = scale.y(p.feasibility);
  return (
    <g
      className={`opp-dot dot-${kind}${colour ? ` ${colour}` : ''}${active ? ' is-active' : ''}`}
      role="link"
      tabIndex={0}
      aria-label={`${p.idea.title}: impact ${formatScore(p.impact)}, feasibility ${formatScore(p.feasibility)}, ${dotKindLabel(
        kind,
      )}, ${p.idea.industry?.name ?? 'unassigned'}`}
      onMouseEnter={() => onActivate(p.idea.id)}
      onMouseLeave={() => onDeactivate(p.idea.id)}
      onFocus={() => onActivate(p.idea.id)}
      onBlur={() => onDeactivate(p.idea.id)}
      onClick={() => onOpen(p.idea.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(p.idea.id);
      }}
    >
      {/* A hit target larger than the mark. */}
      <circle className="opp-hit" cx={cx} cy={cy} r={DOT_R + 6} />
      <path className="opp-mark" d={markPath(kind, cx, cy, active ? DOT_R + 2 : DOT_R)} />
    </g>
  );
}

export function MatrixTip({
  point,
  sameSpot,
  scale,
  width,
}: {
  point: MatrixPoint;
  sameSpot: MatrixPoint[];
  scale: MatrixScale;
  width: number;
}) {
  const { idea } = point;
  const left = scale.x(point.impact);
  return (
    <div className={`opp-tip${left > width * 0.6 ? ' is-left' : ''}`} style={{ left, top: scale.y(point.feasibility) }} role="status">
      <b>{idea.title}</b>
      <span>
        {dotKindLabel(dotKind(point))}
        {idea.industry && ` · ${idea.industry.name}`}
      </span>
      <span className="opp-tip-nums">
        Impact {formatScore(point.impact)} · Feasibility {formatScore(point.feasibility)}
        {idea.total !== null && ` · Total ${formatScore(idea.total)}`}
      </span>
      {sameSpot.length > 0 && (
        <span className="hint">
          Also here: {sameSpot.slice(0, 3).map((p) => p.idea.title).join(', ')}
          {sameSpot.length > 3 && ` and ${sameSpot.length - 3} more`}
        </span>
      )}
    </div>
  );
}

export function MatrixLegend({
  byIndustry,
  industryEntries,
  counts,
}: {
  byIndustry: boolean;
  industryEntries: { colour: string; label: string; count: number }[];
  counts: Record<DotKind, number>;
}) {
  return (
    <ul className="opp-legend" aria-label={byIndustry ? 'Dot colour: industry; dot shape: verdict' : 'Dot colour and shape: verdict'}>
      {byIndustry &&
        industryEntries.map(({ colour, label, count }) => (
          <li key={colour}>
            <Swatch kind="go" colour={colour} />
            {label} <b>{count}</b>
          </li>
        ))}
      {byIndustry && <li className="opp-legend-divider" aria-hidden="true" />}
      {DOT_KINDS.map(({ kind, label }) => (
        <li key={kind}>
          <Swatch kind={kind} colour={byIndustry ? 'series-neutral' : undefined} />
          {label} <b>{counts[kind]}</b>
        </li>
      ))}
    </ul>
  );
}
